import React from 'react';

import { DirectionsRun, EditLocationAlt, FileDownload, Person } from '@mui/icons-material';
import { useRecordContext } from 'components/record/useRecordContext';
import { ResourceContextProvider } from 'components/record/ResourceContext';
import { ColorField, DateField, ListField, CheckField, FieldProps, getAttributeFromObject } from 'components/list/Fields';
import List from 'components/list/List';
import CreateButton from 'components/forms/CreateButton';
import CompetitorForm from './CompetitorForm';
import { useFetch } from 'hooks/useFetchData';
import { DeviceType } from 'components/Devices/DevicesPage';
import ActionMenu from 'components/list/actionMenu/ActionMenu';
import ActionMenuItemEdit from 'components/list/actionMenu/ActionMenuItemEdit';
import ActionMenuItemDelete from 'components/list/actionMenu/ActionMenuItemDelete';
import ActionMenuItemTogglePrivate from 'components/list/actionMenu/ActionMenuItemTogglePrivate';
import CompetitorUserGroupForm from './CompetitorUserGroupForm';
import ActionMenuItemDialog from 'components/list/actionMenu/ActionMenuItemDialog';
import { ActionMenuItemProps } from 'components/list/actionMenu/ActionMenuItem';
import CompetitorTrackForm from 'components/MyEvents/Track/CompetitorTrackForm';
import ExportTrack from 'components/MyEvents/Track/ExportTrack';
import dayjs from 'dayjs';
import { Loader } from 'components/common/General';
import BulkDelete from 'components/forms/BulkDelete';
import { Box, TableCell, Tooltip } from '@mui/material';
import BulkExport from 'components/forms/BulkExport';
import BulkUpdateTracks from 'components/forms/BulkUpdateTracks';
import ActionButton from 'components/common/ActionButton';
import ImportCompetitors from './ImportCompetitors';
import ImportLiveloxCompetitorsButton from './ImportLiveloxCompetitorsButton';
import CreateVirtualCompetitorButton from './CreateVirtualCompetitorButton';
import { SubEventType } from 'types/App';
import useConfiguration from 'hooks/useConfiguration';

type ExportTrackMenuItemProps = ActionMenuItemProps & {
  subEvent: SubEventType;
};

const CompetitorNameField = ({ source = 'name' }: FieldProps) => {
  const { record: competitor } = useRecordContext();

  const userName = competitor.user ? `${competitor.user.firstName} ${competitor.user.lastName}`.trim() : undefined;

  return (
    <TableCell>
      <Box display="flex" alignItems="center" gap={0.5}>
        {getAttributeFromObject(competitor, source)}
        {competitor.userId && (
          <Tooltip title={userName || 'Linked user'}>
            <Person fontSize="small" color="action" />
          </Tooltip>
        )}
      </Box>
    </TableCell>
  );
};

const ExportTrackMenuItem = ({ subEvent, ...rest }: ExportTrackMenuItemProps) => {
  const { record: competitor } = useRecordContext();

  if (competitor.isVirtual) {
    return null;
  }

  return (
    <ActionMenuItemDialog
      label="Export"
      icon={<FileDownload />}
      confirmTitle="Export Track"
      confirmContent={<ExportTrack subEvent={subEvent} />}
      {...rest}
    />
  );
};

const Competitors = () => {
  const { record: subEvent } = useRecordContext();
  const disableStrava = useConfiguration<boolean>('disable_strava') ?? true;

  const defaultValues = {
    startTime: dayjs(subEvent.event.startDate)
  };

  const { data: deviceData, loading } = useFetch<{ items: DeviceType[] }>('GET_LIST', 'Device');

  const deviceList: { id: number; label: string }[] = React.useMemo(() => {
    return deviceData?.items?.filter((device) => !device.isVirtual).map((device) => ({ id: device.id!, label: device.name! })) || [];
  }, [deviceData]);

  if (loading) {
    return <Loader />;
  }

  return (
    <ResourceContextProvider resource="Competitor" params={{ subEventId: subEvent.id }}>
      <List
        bulkOptions
        hidePagination
        title="Competitors"
        avatar={<DirectionsRun />}
        isOrderable
        columnVisibility
        toolbar={
          <React.Fragment>
            <CreateButton label="Add Competitor" defaultValues={defaultValues}>
              <CompetitorForm subEvent={subEvent} devices={deviceList} />
            </CreateButton>
            <CreateButton label="Add from Group" dialogTitle="Add from User Group" many>
              <CompetitorUserGroupForm />
            </CreateButton>
            <ImportCompetitors />
            {subEvent.liveloxUrl && <ImportLiveloxCompetitorsButton subEventId={subEvent.id} />}
            {!disableStrava && <ActionButton method="extractManyTracks" label="Check for Tracks" variables={{ subEventId: subEvent.id }} />}
            <CreateVirtualCompetitorButton subEvent={subEvent} />
          </React.Fragment>
        }
        bulkActions={
          <Box gap={2} display="flex">
            <BulkExport transformProperties={['name', 'shortName', 'color', 'siCard', 'deviceId', 'userId']} />
            <BulkDelete />
            <BulkUpdateTracks />
          </Box>
        }
      >
        <CompetitorNameField source="name" label="Name" />
        <ListField source="device.name" label="Device" />
        <ColorField source="color" label="Color" />
        <DateField source="startTime" label="Start" format="HH:mm:ss" />
        {subEvent.event.settings.useSI && <ListField source="siCard" label="SI Card" />}
        {subEvent.event.status === 'finished' && <CheckField source="hasTrack" label="Track" />}
        <ActionMenu label="Actions" align="right" width="5%">
          <ActionMenuItemTogglePrivate permission="event:manage_competitors" />
          <ActionMenuItemDialog
            label="Track"
            icon={<EditLocationAlt />}
            confirmTitle="Track"
            confirmContent={<CompetitorTrackForm subEvent={subEvent} />}
            permission="event:manage_competitors"
          />
          <ExportTrackMenuItem subEvent={subEvent} permission="event:manage_competitors" />
          <ActionMenuItemEdit
            confirmContent={<CompetitorForm subEvent={subEvent} devices={deviceList} />}
            permission="event:manage_competitors"
          />
          <ActionMenuItemDelete permission="event:manage_competitors" />
        </ActionMenu>
      </List>
    </ResourceContextProvider>
  );
};

export default Competitors;
